import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BadgeCheckIcon, Loader2Icon } from 'lucide-react';
import { ChoiceChips } from './ChoiceChips';
import { OWNER_FORM_ENDPOINT, OwnerFormConfig } from '../data/ownerForms';
import { trackCustom, identifyUser } from '../utils/tracking';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const inputClass = 'mt-2 w-full border-2 border-white/20 bg-ink-950 px-4 py-3 text-[16px] text-offwhite placeholder:text-steel-500 focus:border-grit-500 focus:outline-none';
const labelClass = 'text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400';

/** One Owner Center form. Answers are emailed to support@gritvuegear.com. */
export function OwnerForm({
  config
}: {
  config: OwnerFormConfig;
}) {
  const empty = useMemo(() => Object.fromEntries(config.fields.map(f => [f.name, ''])), [config]);
  const [values, setValues] = useState<Record<string, string>>(empty);
  const [status, setStatus] = useState<Status>('idle');
  const started = useRef(false);
  const set = (name: string, value: string) => {
    if (!started.current) {
      started.current = true;
      trackCustom('owner_form_started', {
        form: config.id
      });
    }
    setValues(prev => ({
      ...prev,
      [name]: value
    }));
    if (status === 'error') setStatus('idle');
  };
  const missing = config.fields.filter(f => f.required && !values[f.name]?.trim());
  const emailField = config.fields.find(f => f.kind === 'email');
  const emailValid = !emailField || /\S+@\S+\.\S+/.test(values[emailField.name] ?? '');
  const canSubmit = missing.length === 0 && emailValid;
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || status === 'submitting') return;
    setStatus('submitting');
    const reference = values['Order number'] ? ` · Order ${values['Order number']}` : '';
    const payload: Record<string, string> = {
      _subject: `${config.subject}${reference}`,
      _template: 'table',
      _captcha: 'false',
      Form: config.title,
      ...Object.fromEntries(Object.entries(values).map(([k, v]) => [k, v.trim() || '—'])),
      'Submitted at': new Date().toISOString()
    };
    if (emailField) payload._replyto = values[emailField.name];
    try {
      const response = await fetch(OWNER_FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(String(response.status));
      const result = (await response.json().catch(() => ({}))) as {
        success?: string | boolean;
      };
      if (result.success === false || result.success === 'false') throw new Error('not_delivered');
      identifyUser({
        email: emailField ? values[emailField.name] : undefined,
        phone: values['Phone'],
        name: values['Full name']
      });
      trackCustom('owner_form_submitted', {
        form: config.id
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <motion.div role="status" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.22,
      ease: [0.23, 1, 0.32, 1]
    }} className="border-2 border-grit-500 bg-grit-500/10 p-6 sm:p-8" data-id="element-1009" data-mp="3giinm28v59ii">
        <p className="flex items-center gap-2 font-display text-3xl font-extrabold uppercase text-grit-500" data-id="element-1010" data-mp="3pjq7vgejo9tl">
          <BadgeCheckIcon className="h-8 w-8" aria-hidden="true" data-id="element-1011" data-mp="31tvzpc5679fo" />
          {config.successTitle}
        </p>
        <p className="mt-2 text-[15px] text-steel-200" data-id="element-1012" data-mp="2wsge9iz2z0wr">{config.successBody}</p>
        <button type="button" onClick={() => {
        setValues(empty);
        setStatus('idle');
        started.current = false;
      }} className="mt-5 text-[13px] font-bold uppercase tracking-[0.16em] text-grit-500 underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-1013" data-mp="3bl0dw9n0uq9g">
          Send another
        </button>
      </motion.div>;
  }
  return <form onSubmit={submit} noValidate className="grid gap-6 border border-white/12 bg-ink-900 p-6 sm:p-8" data-id="element-1014" data-mp="1mb81hn2glk3o">
      <div data-id="element-1015" data-mp="1qd2y4e9g39b4">
        <h2 className="font-display text-3xl font-extrabold uppercase leading-none text-offwhite" data-id="element-1016" data-mp="1qcjhd0vj6qyo">{config.title}</h2>
        <p className="mt-2 text-[15px] leading-relaxed text-steel-300" data-id="element-1017" data-mp="1v5mgrpmzoums">{config.intro}</p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2" data-id="element-1018" data-mp="202wbnkt5dyi0">
        {config.fields.map(field => {
        const id = `${config.id}-${field.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
        if (field.kind === 'chips') {
          return <div key={field.name} className="sm:col-span-2" data-id="element-1019" data-mp="2e3fiiolvmyil">
                <ChoiceChips legend={field.label} options={field.options} value={values[field.name]} onChange={v => set(field.name, v)} optional={!field.required} data-id="element-1020" data-mp="2e0t88h7lq4wo" />
              </div>;
        }
        if (field.kind === 'textarea') {
          return <div key={field.name} className="sm:col-span-2" data-id="element-1021" data-mp="7dc5jmmcr4hd">
                <label htmlFor={id} className={labelClass} data-id="element-1022" data-mp="2esjznbo7upj">{field.label}</label>
                <textarea id={id} rows={4} required={field.required} value={values[field.name]} onChange={e => set(field.name, e.target.value)} placeholder={field.placeholder} className={`${inputClass} resize-y`} data-id="element-1023" data-mp="3ujmnhktbj5vb" />
              </div>;
        }
        return <div key={field.name} className={field.half ? '' : 'sm:col-span-2'} data-id="element-1024" data-mp="h59lf6ob2kmf">
              <label htmlFor={id} className={labelClass} data-id="element-1025" data-mp="36ubh9nt78j9s">{field.label}</label>
              <input id={id} type={field.kind} required={field.required} value={values[field.name]} onChange={e => set(field.name, e.target.value)} placeholder={field.placeholder} autoComplete={field.autoComplete ?? 'off'} className={inputClass} data-id="element-1026" data-mp="31up4ffha951i" />
            </div>;
      })}
      </div>

      {config.note && <p className="border-l-4 border-grit-500 bg-ink-950 px-4 py-3 text-[13px] leading-relaxed text-steel-300" data-id="element-1027" data-mp="2ybgniaspni2i">{config.note}</p>}

      <div data-id="element-1028" data-mp="2kjjvs7uyfa6n">
        <button type="submit" disabled={!canSubmit || status === 'submitting'} className="flex w-full items-center justify-center gap-2 bg-grit-500 px-5 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offwhite" data-id="element-1029" data-mp="2pc3aj85dobd1">
          {status === 'submitting' && <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1030" data-mp="1mzsdenaejr0r" />}
          {status === 'submitting' ? 'Sending...' : config.submitLabel}
        </button>
        {!canSubmit && <p className="mt-2 text-[12px] text-steel-500" data-id="element-1031" data-mp="1i2tlmbe7nw5q">Fill in the required fields to continue.</p>}
        {status === 'error' && <p role="alert" className="mt-4 border border-white/20 bg-white/[0.03] p-4 text-[14px] text-steel-200" data-id="element-1032" data-mp="1rz527dku385z">
            Something went wrong and your message wasn&apos;t sent. Try again, or email support@gritvuegear.com.
          </p>}
      </div>
    </form>;
}